// EP-133 T133-019 — visitor widget admin config (staff+). Owner ruling §9a:
// this whole surface — create/enable/disable, editing availability, AND
// rotating the public key — is staff+, DELIBERATELY LOOSER than EP-105's
// ParkingZonePolicy#rotate_public_key? (admin+). Do not "fix" that divergence
// here; it is a recorded owner decision (VisitorWidgetPolicy carries the same
// note server-side). Route-level accountant blocking (AccountantBlockedRoute
// in App.tsx) is the only role gate this page needs — every other role is
// staff+ by construction.
//
// ⚠️ Backend contract for index/create/update/toggle/update_availability is
// ASSUMED — see the header comment in web/src/hooks/useVisitorWidgets.ts.
// Only rotate_public_key is real as of this writing.
import { useEffect, useState } from 'react'
import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { Copy, Check, RefreshCw } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, Input, Toggle, FormActions, ConfirmDialog, EmptyState, Alert } from '@/components/ui'
import { useNotificationStore } from '@/stores'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import { useProperty } from '@/hooks/useProperties'
import {
  useVisitorWidgets,
  useCreateVisitorWidget,
  useUpdateVisitorWidget,
  useUpdateVisitorWidgetAvailability,
  useToggleVisitorWidget,
  useRotateVisitorWidgetKey,
} from '@/hooks/useVisitorWidgets'
import { WeekdayAvailabilityEditor } from './WeekdayAvailabilityEditor'
import { validateAvailability } from './availabilityValidation'
import type { WeeklyAvailability } from '@/types/visitor'

interface SlotSettingsFormData {
  slot_minutes: number
  lead_time_minutes: number
  max_per_slot: number
  booking_window_days: number
}

/** Assembles the public `/visit/:publicKey` iframe URL — see D-1 (the widget
 * is an iframe to a FlowRent-hosted route, never a JS bundle). The web route
 * itself is T133-020's scope; this builds against the exact path the
 * T133-019 design wireframe shows (".../visit/wgt_xxx"). */
function embedUrlFor(publicKey: string): string {
  return `${window.location.origin}/visit/${publicKey}`
}

export function VisitorWidgetSettingsPage() {
  const { t } = useTranslation('visitors')
  const { t: tc } = useTranslation('common')
  const { propertyId } = useParams<{ propertyId: string }>()
  const resolvedPropertyId = Number(propertyId)

  const addToast = useNotificationStore((s) => s.addToast)
  const translateError = useApiErrorTranslator('visitors')

  const { data: property } = useProperty(resolvedPropertyId)
  const { data: widgets, isLoading, isFetching } = useVisitorWidgets(resolvedPropertyId)
  const widget = widgets?.[0] ?? null

  const createWidget = useCreateVisitorWidget()
  const updateWidget = useUpdateVisitorWidget()
  const updateAvailability = useUpdateVisitorWidgetAvailability()
  const toggleWidget = useToggleVisitorWidget()
  const rotateKey = useRotateVisitorWidgetKey()

  const [availability, setAvailability] = useState<WeeklyAvailability>({})
  const [seededAvailability, setSeededAvailability] = useState<WeeklyAvailability>({})
  const [availabilityError, setAvailabilityError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [rotateConfirmOpen, setRotateConfirmOpen] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty: isSettingsDirty },
  } = useForm<SlotSettingsFormData>({
    defaultValues: {
      slot_minutes: 30,
      lead_time_minutes: 0,
      max_per_slot: 1,
      booking_window_days: 14,
    },
  })

  // Re-seed both the RHF settings form and the local availability state
  // whenever the loaded widget changes (initial load, or after a save
  // round-trips through the query cache).
  useEffect(() => {
    if (!widget) return
    reset({
      slot_minutes: widget.slot_minutes,
      lead_time_minutes: widget.lead_time_minutes,
      max_per_slot: widget.max_per_slot,
      booking_window_days: widget.booking_window_days,
    })
    setAvailability(widget.availability)
    setSeededAvailability(widget.availability)
    setAvailabilityError(null)
  }, [widget, reset])

  const isAvailabilityDirty = JSON.stringify(availability) !== JSON.stringify(seededAvailability)
  const isDirty = isSettingsDirty || isAvailabilityDirty
  const isSaving = updateWidget.isPending || updateAvailability.isPending

  const handleCreate = async () => {
    try {
      await createWidget.mutateAsync({ property_id: resolvedPropertyId })
      addToast({ type: 'success', message: t('messages.widget_created') })
    } catch (err) {
      addToast({ type: 'error', message: translateError(err, { fallback: t('messages.create_error') }) })
    }
  }

  const handleToggleEnabled = async (enabled: boolean) => {
    if (!widget) return
    try {
      await toggleWidget.mutateAsync({ id: widget.id, enabled })
      addToast({ type: 'success', message: t(enabled ? 'messages.enabled_on' : 'messages.enabled_off') })
    } catch (err) {
      addToast({ type: 'error', message: translateError(err, { fallback: t('messages.toggle_error') }) })
    }
  }

  const handleCopyEmbed = async () => {
    if (!widget?.public_key) return
    const url = embedUrlFor(widget.public_key)
    const snippet = `<iframe src="${url}" width="100%" height="640" style="border:0" title="${t('page_title')}"></iframe>`
    await navigator.clipboard.writeText(snippet)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  // Rotating an EXISTING key invalidates the live embedded iframe on the
  // operator's own website, so it goes through ConfirmDialog. Issuing the
  // very first key (public_key still null) has nothing to invalidate — same
  // distinction ParkingZone's PublicCheckInStickerCard draws between
  // "generate" and "rotate", just with a confirm added to the rotate leg per
  // the T133-019 design.
  const handleRotateClick = () => {
    if (!widget) return
    if (widget.public_key) {
      setRotateConfirmOpen(true)
    } else {
      void doRotate()
    }
  }

  const doRotate = async () => {
    if (!widget) return
    try {
      await rotateKey.mutateAsync(widget.id)
      addToast({ type: 'success', message: t('messages.key_rotated') })
    } catch (err) {
      addToast({ type: 'error', message: translateError(err, { fallback: t('messages.rotate_error') }) })
    } finally {
      setRotateConfirmOpen(false)
    }
  }

  const onSave = handleSubmit(async (formData) => {
    if (!widget) return
    const validationError = validateAvailability(availability, t)
    if (validationError) {
      setAvailabilityError(validationError)
      return
    }
    setAvailabilityError(null)
    try {
      await Promise.all([
        updateAvailability.mutateAsync({ id: widget.id, availability }),
        updateWidget.mutateAsync({
          id: widget.id,
          slot_minutes: Number(formData.slot_minutes),
          lead_time_minutes: Number(formData.lead_time_minutes),
          max_per_slot: Number(formData.max_per_slot),
          booking_window_days: Number(formData.booking_window_days),
        }),
      ])
      addToast({ type: 'success', message: t('messages.settings_saved') })
    } catch (err) {
      addToast({ type: 'error', message: translateError(err, { fallback: t('messages.save_error') }) })
    }
  })

  const onCancel = () => {
    if (!widget) return
    reset({
      slot_minutes: widget.slot_minutes,
      lead_time_minutes: widget.lead_time_minutes,
      max_per_slot: widget.max_per_slot,
      booking_window_days: widget.booking_window_days,
    })
    setAvailability(widget.availability)
    setAvailabilityError(null)
  }

  const breadcrumbItems = [
    { label: tc('nav.properties'), href: '/properties' },
    { label: property?.name ?? '...', href: `/properties/${resolvedPropertyId}` },
    { label: t('page_title') },
  ]

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <div className="h-64 animate-pulse bg-base-200 rounded" />
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs items={breadcrumbItems} />

      <PageHeader title={t('page_title')} isRefreshing={isFetching && !isLoading} />

      {!widget ? (
        <EmptyState
          title={t('widget.create_title')}
          description={t('widget.create_description')}
          action={
            <Button onClick={() => void handleCreate()} loading={createWidget.isPending}>
              {t('widget.create_action')}
            </Button>
          }
        />
      ) : (
        <>
          <Card
            actions={
              <Toggle
                variant="primary"
                label={t('widget.enabled_label')}
                checked={widget.enabled}
                disabled={toggleWidget.isPending}
                onChange={(e) => void handleToggleEnabled(e.target.checked)}
              />
            }
          >
            <div className="space-y-4">
              {/* Embed link + key — live actions, independent of the form's
                  Cancel/Save (they take effect the moment staff acts). */}
              <div className="space-y-1">
                <p className="text-xs font-medium text-base-content/60">{t('widget.embed_link_label')}</p>
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-mono text-xs break-all bg-base-200 rounded px-2 py-1.5 flex-1 min-w-0">
                    {widget.public_key ? embedUrlFor(widget.public_key) : t('widget.no_key_yet')}
                  </p>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={!widget.public_key}
                    leftIcon={copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    onClick={() => void handleCopyEmbed()}
                  >
                    {copied ? t('widget.copied') : t('widget.copy')}
                  </Button>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-base-200">
                <div>
                  <p className="text-xs font-medium text-base-content/60">{t('widget.public_key_label')}</p>
                  <p className="font-mono text-sm">{widget.public_key ?? '—'}</p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  leftIcon={<RefreshCw className="w-3 h-3" />}
                  loading={rotateKey.isPending}
                  onClick={handleRotateClick}
                >
                  {widget.public_key ? t('widget.rotate_key') : t('widget.generate_key')}
                </Button>
              </div>
            </div>
          </Card>

          <Card>
            <WeekdayAvailabilityEditor
              availability={availability}
              onChange={(next) => {
                setAvailability(next)
                setAvailabilityError(null)
              }}
              disabled={isSaving}
            />

            {availabilityError && (
              <Alert variant="error" className="mt-4">
                {availabilityError}
              </Alert>
            )}

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mt-6">
              <Input
                label={t('settings.slot_minutes_label')}
                type="number"
                min={1}
                className="min-w-0"
                error={errors.slot_minutes?.message}
                {...register('slot_minutes', {
                  required: tc('errors.required'),
                  min: { value: 1, message: tc('errors.required') },
                  valueAsNumber: true,
                })}
              />
              <Input
                label={t('settings.lead_time_minutes_label')}
                type="number"
                min={0}
                className="min-w-0"
                error={errors.lead_time_minutes?.message}
                {...register('lead_time_minutes', {
                  required: tc('errors.required'),
                  min: { value: 0, message: tc('errors.required') },
                  valueAsNumber: true,
                })}
              />
              <Input
                label={t('settings.max_per_slot_label')}
                type="number"
                min={1}
                className="min-w-0"
                error={errors.max_per_slot?.message}
                {...register('max_per_slot', {
                  required: tc('errors.required'),
                  min: { value: 1, message: tc('errors.required') },
                  valueAsNumber: true,
                })}
              />
              <Input
                label={t('settings.booking_window_days_label')}
                type="number"
                min={1}
                className="min-w-0"
                error={errors.booking_window_days?.message}
                {...register('booking_window_days', {
                  required: tc('errors.required'),
                  min: { value: 1, message: tc('errors.required') },
                  valueAsNumber: true,
                })}
              />
            </div>

            <FormActions>
              <Button type="button" variant="ghost" onClick={onCancel} disabled={!isDirty || isSaving}>
                {tc('actions.cancel')}
              </Button>
              <Button type="button" onClick={() => void onSave()} loading={isSaving} disabled={!isDirty}>
                {tc('actions.save')}
              </Button>
            </FormActions>
          </Card>
        </>
      )}

      <ConfirmDialog
        isOpen={rotateConfirmOpen}
        onClose={() => setRotateConfirmOpen(false)}
        onConfirm={() => void doRotate()}
        title={t('widget.rotate_confirm_title')}
        message={t('widget.rotate_confirm_message')}
        confirmLabel={t('widget.rotate_key')}
        cancelLabel={tc('actions.cancel')}
        variant="warning"
        loading={rotateKey.isPending}
      />
    </div>
  )
}
